import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Inline-edit autocomplete sized for a DataGrid cell — flush with the cell
 * edges, no label, no outline. Supports either a fixed `options` array
 * (filtered locally) or async `loadOptions(search)`.
 *
 * Props:
 *   options       – array, when known up-front
 *   loadOptions   – async (search) => array, when results come from an API
 *   getOptionLabel    – option => string (used in the input + matching)
 *   getOptionKey      – option => stable id (defaults to opt.id ?? opt.value)
 *   renderOptionRow   – (option, inputValue) => ReactNode for the dropdown row
 *                       layout; inputValue is the current search text so rows
 *                       can highlight the match
 *   onSelect          – option | null => void (called on pick)
 *   onCancel          – () => void (called on Escape / blur without picking)
 *   placeholder       – string
 *   disableOption     – option => boolean (e.g. already-selected exclusions)
 */
export default function CellAutocomplete({
  options: fixedOptions,
  loadOptions,
  getOptionLabel,
  getOptionKey = opt => opt?.id ?? opt?.value ?? null,
  renderOptionRow,
  onSelect,
  onCancel,
  placeholder = 'Search…',
  disableOption,
}) {
  const isAsync = typeof loadOptions === 'function';
  const [inputValue, setInputValue] = useState('');
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const triggerLoad = useCallback(
    async search => {
      if (!isAsync) return;
      setLoading(true);
      try {
        const results = await loadOptions(search);
        setAsyncOptions(Array.isArray(results) ? results : []);
      } finally {
        setLoading(false);
      }
    },
    [isAsync, loadOptions],
  );

  // Prime the async list once on mount.
  useEffect(() => {
    if (isAsync) triggerLoad('');
  }, [isAsync, triggerLoad]);

  const debouncedLoad = useMemo(() => {
    let timeoutId;
    return search => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => triggerLoad(search), 250);
    };
  }, [triggerLoad]);

  const options = isAsync ? asyncOptions : (fixedOptions ?? []);

  return (
    <Autocomplete
      autoFocus
      openOnFocus
      open
      disablePortal={false}
      fullWidth
      size="small"
      loading={loading}
      options={options}
      inputValue={inputValue}
      onInputChange={(_e, val, reason) => {
        setInputValue(val);
        if (reason === 'input' && isAsync) debouncedLoad(val);
      }}
      filterOptions={isAsync ? x => x : undefined}
      getOptionLabel={opt => (opt ? getOptionLabel(opt) : '')}
      isOptionEqualToValue={(opt, v) => getOptionKey(opt) === getOptionKey(v)}
      getOptionDisabled={disableOption}
      onChange={(_e, picked) => {
        onSelect?.(picked ?? null);
      }}
      onClose={(_e, reason) => {
        if (reason === 'escape') onCancel?.();
      }}
      renderOption={(optionProps, option) => (
        <Box
          component="li"
          {...optionProps}
          key={getOptionKey(option)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            py: 0.75,
            px: 1.25,
            borderBottom: 1,
            borderColor: 'var(--md-outline-variant)',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          {renderOptionRow ? (
            renderOptionRow(option, inputValue)
          ) : (
            <Typography variant="body2">{getOptionLabel(option)}</Typography>
          )}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus
          variant="standard"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            sx: { px: 1, height: '100%', fontSize: '0.875rem' },
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width: '100%',
        height: '100%',
        '& .MuiAutocomplete-inputRoot': { height: '100%', py: 0 },
        '& .MuiAutocomplete-input': { p: 0 },
      }}
      slotProps={{
        paper: { sx: { mt: 0.5, borderRadius: 1 } },
        popper: { sx: { zIndex: theme => theme.zIndex.modal + 1 } },
      }}
    />
  );
}
